import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Receipt, RefreshCw, Utensils, Snowflake, Flame } from 'lucide-react';
import ReceiptEditor from './ReceiptEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  food: { label: 'Comida', icon: Utensils, cls: 'bg-orange-100 text-orange-700' },
  ice: { label: 'Gelo', icon: Snowflake, cls: 'bg-cyan-100 text-cyan-700' },
  shisha: { label: 'Shisha', icon: Flame, cls: 'bg-purple-100 text-purple-700' },
};

const ReceiptsManager = ({ token, language }) => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editEntity, setEditEntity] = useState(null);

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/receipts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setReceipts(await res.json());
    } catch (e) {
      console.debug('receipts fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  return (
    <Card className="rounded-2xl">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Receipt size={18} className="text-[#FF6B35]" />
            {language === 'pt' ? 'Recibos Emitidos' : 'Issued Receipts'} ({receipts.length})
          </span>
          <Button variant="outline" size="sm" className="rounded-full" onClick={fetchReceipts} data-testid="receipts-refresh-btn">
            <RefreshCw size={14} className="mr-1" />
            {language === 'pt' ? 'Atualizar' : 'Refresh'}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-[#9FA2B4] py-6 text-center">{language === 'pt' ? 'A carregar…' : 'Loading…'}</p>
        ) : receipts.length === 0 ? (
          <p className="text-sm text-[#9FA2B4] py-6 text-center">
            {language === 'pt' ? 'Ainda sem recibos — são criados automaticamente quando uma entrega é concluída.' : 'No receipts yet — created automatically when a delivery completes.'}
          </p>
        ) : (
          <div className="space-y-2">
            {receipts.map(r => {
              const meta = TYPE_META[r.order_type] || TYPE_META.food;
              const Icon = meta.icon;
              return (
                <button
                  key={r.receipt_id}
                  onClick={() => setEditEntity(r.entity_id)}
                  className="w-full flex items-center justify-between p-3 bg-[#F4F1EA] rounded-xl text-left hover:ring-2 hover:ring-[#FF6B35]/30 transition-shadow"
                  data-testid={`receipt-row-${r.receipt_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shrink-0">
                      <Icon size={16} className="text-[#FF6B35]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#004E64] truncate">
                        #{r.entity_id?.slice(-8)} · {r.customer_name || '—'}
                      </p>
                      <p className="text-xs text-[#9FA2B4]">
                        {r.created_at ? new Date(r.created_at).toLocaleString('pt-PT') : ''}
                        {(r.adjustments || []).length > 0 && ` · ${r.adjustments.length} ${language === 'pt' ? 'ajuste(s)' : 'adjustment(s)'}`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className="flex items-center gap-1 justify-end mb-1">
                      <Badge className={`${meta.cls} text-xs`}>{meta.label}</Badge>
                      {r.sent_count > 0 ? (
                        <Badge className="bg-green-100 text-green-700 text-xs" title={r.sent_to}>
                          ✓ {language === 'pt' ? 'Enviado' : 'Sent'} {r.sent_count}×
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                          {language === 'pt' ? 'Não enviado' : 'Not sent'}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm font-bold text-[#FF6B35]">€{Number(r.final_total || 0).toFixed(2)}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
      <ReceiptEditor
        entityId={editEntity}
        open={!!editEntity}
        onOpenChange={(o) => { if (!o) { setEditEntity(null); fetchReceipts(); } }}
        token={token}
        language={language}
      />
    </Card>
  );
};

export default ReceiptsManager;
